import React from "react";
import DashboardCalculationHover from "./DashboardCalculationHover.jsx";
import { HoverCard } from "./ResultsPrimitives.jsx";
import { chartLabelIndexes } from "../utils/dashboardChartGeometry.js";
import { resolveComparisonLabel, resolveMetricDelta } from "../utils/dashboardComparison.js";
import { isPartialRevenue, summarizeEconomics } from "../utils/dashboardEconomics.js";

const MONEY = new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 0 });
const PERCENT = new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 1 });
const money = (value) => (value == null ? "n/d" : `${MONEY.format(value)} $`);
const percent = (value) => (value == null ? "n/d" : `${PERCENT.format(value)} %`);
const dayMonth = (date) => `${date.slice(8, 10)}/${date.slice(5, 7)}`;
const plural = (count, word) => `${count} ${word}${count > 1 ? "s" : ""}`;

// The cost per dispatch follows the volume: its change is shown in gray, without a good or bad tone.
// Revenue changes compare only periods whose orders all have a rate.
const FIGURES = [
  { key: "revenue", label: "Revenu prévu / dispatch", value: (s) => s.revenuePerDispatch, unit: "%", higherIsBetter: true, rateBased: true,
    rows: (s) => [["Heures (taux de l’entreprise ou exception)", money(s.hourlyPerDispatch)], ["Suppléments tonne et voyage", money(s.supplementsPerDispatch)],
      ["Montants fixes", money(s.fixedPerDispatch)], ["Tonnes avec un taux", percent(s.coveragePct)]] },
  { key: "cost", label: "Coût / dispatch", value: (s) => s.costPerDispatch, unit: "%", higherIsBetter: null, rows: () => [] },
  { key: "margin", label: "Marge / dispatch", value: (s) => s.marginPerDispatch, delta: (s) => s.marginPct, unit: "pts", higherIsBetter: true, rateBased: true,
    note: (s) => percent(s.marginPct),
    rows: (s) => [["Marge en %", percent(s.marginPct)], ["Marge par heure de mission", s.marginPerHour == null ? "n/d" : `${MONEY.format(s.marginPerHour)} $/h`]] },
];

function EconomicsChart({ days, selectedDates }) {
  const frameRef = React.useRef(null);
  const [measuredWidth, setMeasuredWidth] = React.useState(680);
  React.useEffect(() => {
    const observer = new ResizeObserver(([entry]) => { if (entry.contentRect.width > 0) setMeasuredWidth(entry.contentRect.width); });
    observer.observe(frameRef.current);
    return () => observer.disconnect();
  }, []);
  // Touch screens keep wide days and scroll; a narrow desktop card fits every day in its width.
  const touchLayout = typeof window !== "undefined" && window.matchMedia?.("(max-width: 767px)").matches;
  const minimumWidth = touchLayout && measuredWidth < 600 ? Math.max(480, days.length * 28 + 56) : 0;
  const width = Math.max(minimumWidth, measuredWidth);
  const left = 56, right = width - 8, top = 8, bottom = top + 170, height = bottom + 30;
  const slot = (right - left) / Math.max(days.length, 1);
  const peak = Math.max(1, ...days.flatMap((day) => [day.plannedRevenueCad || 0, day.realCostCad || 0]));
  const magnitude = 10 ** Math.floor(Math.log10(peak / 4));
  const step = Math.ceil(peak / 4 / magnitude) * magnitude;
  const y = (value) => bottom - (value / (step * 4)) * (bottom - top);
  const labels = chartLabelIndexes(days.length, right - left);
  const barWidth = Math.min(12, slot * 0.32);
  return <div ref={frameRef} className="fleet-chart-scroll" data-dashboard-chart-frame="true" role="region" aria-label="Revenu prévu et coût par dispatch">
    <div className="fleet-chart-canvas" style={{ minWidth: minimumWidth }}>
      <svg viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
        {[0, 1, 2, 3, 4].map((index) => <g key={index}>
          <line x1={left} x2={right} y1={y(index * step)} y2={y(index * step)} className="fleet-chart-grid" />
          <text x={left - 7} y={y(index * step) + 4} textAnchor="end" className="fleet-chart-label">{MONEY.format(index * step)} $</text>
        </g>)}
        {days.map((day, index) => {
          const center = left + slot * (index + 0.5);
          return <g key={day.dispatchDate} data-context={selectedDates.has(day.dispatchDate) ? undefined : "true"}>
            {day.plannedRevenueCad != null ? <rect data-economics-bar="revenue" x={center - barWidth - 1} y={y(day.plannedRevenueCad)} width={barWidth}
              height={bottom - y(day.plannedRevenueCad)} rx="2" className="economics-bar-revenue" /> : null}
            {day.realCostCad != null ? <rect data-economics-bar="cost" x={center + 1} y={y(day.realCostCad)} width={barWidth}
              height={bottom - y(day.realCostCad)} rx="2" className="economics-bar-cost" /> : null}
            {day.plannedRevenueCad == null && day.realCostCad == null ? <text x={center} y={bottom - 6} textAnchor="middle" className="fleet-chart-label">—</text> : null}
            {labels.has(index) &&
              <text x={center} y={bottom + 22} textAnchor={index === 0 ? "start" : index === days.length - 1 ? "end" : "middle"} className="fleet-chart-label">{day.dateLabel || dayMonth(day.dispatchDate)}</text>}
          </g>;
        })}
      </svg>
      {days.map((day, index) => {
        const margin = day.plannedRevenueCad != null && day.realCostCad != null ? day.plannedRevenueCad - day.realCostCad : null;
        const rows = [["Revenu prévu", money(day.plannedRevenueCad)], ["Coût", money(day.realCostCad)], ["Marge", money(margin)],
          ["Tonnes avec un taux", percent(day.revenueCoveragePct)], ...(day.ordersWithoutRate ? [["Commandes sans taux", String(day.ordersWithoutRate)]] : [])];
        return <HoverCard key={day.dispatchDate} title={`Dispatch du ${dayMonth(day.dispatchDate)}`} referenceAsChild flat accentColor="var(--color-accent)" placement="top" maxWidth={300} delay={100}
          content={<dl className="dashboard-kpi-rows">{rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>}>
          <span className="fleet-day-target rounded-[4px]" data-economics-day={day.dispatchDate} tabIndex={0}
            style={{ left: `${(left + slot * index) / width * 100}%`, width: `${slot / width * 100}%`, top: `${top / height * 100}%`, height: `${(bottom - top) / height * 100}%` }}
            aria-label={`${dayMonth(day.dispatchDate)} : revenu prévu ${money(day.plannedRevenueCad)}, coût ${money(day.realCostCad)}`} />
        </HoverCard>;
      })}
    </div>
  </div>;
}

export default function DashboardEconomicsCard({ period, contextDays = [] }) {
  const summary = summarizeEconomics(period.days || []);
  const previous = summarizeEconomics(period.previous?.days || []);
  const selectedDates = new Set((period.days || []).map((day) => day.dispatchDate));
  return <section className="dashboard-card dashboard-economics rounded-[4px]" aria-label="Revenu et coût">
    <header className="dashboard-card-header">
      <div><h2>Revenu et coût</h2><p className="dashboard-economics-scope">Revenu prévu et coût réel estimé, par dispatch · {period.label}</p></div>
      <div className="fleet-card-tools"><div className="fleet-legend">
        <span><i className="economics-swatch-revenue" aria-hidden="true" />Revenu prévu</span>
        <span><i className="economics-swatch-cost" aria-hidden="true" />Coût</span>
      </div></div>
    </header>
    <div className="dashboard-economics-body">
    <div className="dashboard-economics-summary">
    {summary ? <div className="dashboard-economics-figures">
      {FIGURES.map((figure) => {
        const text = money(figure.value(summary));
        const masked = Boolean(previous) && figure.rateBased && (isPartialRevenue(summary) || isPartialRevenue(previous));
        const delta = previous && !masked ? resolveMetricDelta((figure.delta || figure.value)(summary), (figure.delta || figure.value)(previous), figure) : null;
        const rows = [...figure.rows(summary), ...(previous ? [["Période précédente", money(figure.value(previous))]] : [])];
        return <DashboardCalculationHover key={figure.key} label={figure.label} value={text} hoverCard={rows.length ? { content: <dl className="dashboard-kpi-rows">
          {rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
        </dl> } : null}>
          <div className="dashboard-economics-figure" data-dashboard-economics={figure.key} data-sign={figure.key === "margin" && summary.marginPerDispatch < 0 ? "negative" : undefined}>
            <span className="dashboard-kpi-label">{figure.label}</span>
            <span className="dashboard-economics-value">{text}{figure.note ? <small> · {figure.note(summary)}</small> : null}</span>
            <span className="dashboard-kpi-delta">{delta ? <><span data-tone={delta.tone}>{delta.label}</span> <span className="dashboard-kpi-compare">{resolveComparisonLabel(period, previous.dayCount)}</span></>
              : masked ? <span className="dashboard-kpi-compare">écart masqué : revenu partiel</span> : null}</span>
          </div>
        </DashboardCalculationHover>;
      })}
    </div> : <p className="dashboard-economics-empty" role="status">Revenu prévu indisponible pour cette période : aucun dispatch n’a à la fois un coût et des tarifs lus.</p>}
    {isPartialRevenue(summary) ? <p className="dashboard-economics-partial" role="note">
      Revenu partiel : {plural(summary.ordersWithoutRate, "commande")} sans taux ({percent(summary.coveragePct)} des tonnes couvertes). La marge est sous-estimée.{" "}
      <a className="dashboard-watch-link" href="/billing?tab=accounts">Compléter les taux des entreprises →</a>
    </p> : null}
    </div>
    <EconomicsChart days={contextDays} selectedDates={selectedDates} />
    </div>
  </section>;
}
